// Layer registry — the catalog of overlays the Intel map can render.
//
// Each LayerSpec is intentionally explicit: no factory, no abstract base.
// Every entry exports its own deck.gl `Layer` constructor so the registry
// stays inspectable and grep-friendly. Consumers pass `(rows, viewport)`
// and the registry returns a deck.gl Layer instance ready to drop into
// MapboxOverlay.
//
// Composition order (per design doc lines 84-95): heatmap (z<5) → density
// hex (z 5-8) → icon (z>8). The map composes the visible layers in one
// pass; per-layer visibility is decided by `visibleAtZoom`.
//
// Iron Rule 11: registry-driven, not page-bespoke. Vendor / Agency / Award
// maps reuse the same registry entries.

import type { Layer } from '@deck.gl/core';
import type { intel as i } from '@eidollon/contracts';

export type LayerKind =
  | 'icon'
  | 'hex'
  | 'heatmap'
  | 'contracts-region'
  /**
   * @deprecated Replaced by `'contracts-region'` (T11). The HeatmapLayer KDE
   * over province centroids is superseded by region-polygon choropleths
   * served from the `/v1/regions` MVT tiles. Kept in the union for one
   * release so external consumers typing against this string don't break;
   * the layer is no longer registered in `INTEL_LAYERS`. Remove next release.
   */
  | 'contracts-hex';
export type Classification = 'OPEN' | 'CUI' | 'RESTRICTED';

export interface Viewport {
  zoom: number;
  /** [west, south, east, north]; optional — layers may use it for LOD. */
  bbox?: [number, number, number, number];
}

/** One geocoded contract aggregate from `/v1/map/contracts`. The endpoint
 *  emits one row per (province, jurisdiction); `count` is the contract
 *  total in that bin. The contracts-hex layer treats `count` as the weight
 *  when binning into H3 cells, so today's province-centroid rows still
 *  produce a sensible heatmap. When the real geocoding pipeline lands
 *  (one row per contract place_of_performance), the same layer ramps up
 *  to true zoom-level subdivision automatically. */
export interface ContractMapPoint {
  id: string;
  kind: 'contract';
  lat: number;
  lng: number;
  title: string;
  subtitle: string;
  value: number | null;
  ts: string | null;
  href: string | null;
  count: number;
}

/** Per-region properties carried on each `/v1/map/regions` (T9) feature.
 *  `contract_count` drives the blue choropleth fill; the rest ride along for
 *  tooltips / future ramps. */
export interface RegionMetric {
  region_id: string;
  name?: string;
  level?: string;
  contract_count: number;
  total_award_usd?: number | null;
  last_signal_ts?: string | null;
}

/** A region polygon + its metrics — one feature from the `/v1/map/regions`
 *  FeatureCollection (T9). The `contracts-region` layer renders these
 *  directly with deck.gl `GeoJsonLayer`: geometry from `geometry`, fill from
 *  `properties.contract_count`. Geometry + metric arrive in ONE response, so
 *  no client-side tile↔metric join is needed (the seed is ~400 polygons —
 *  GeoJsonLayer beats MVT tiles at this scale; T8's MVT route stays for the
 *  future 10k+ region case). */
export interface RegionFeature {
  type: 'Feature';
  // biome-ignore lint/suspicious/noExplicitAny: GeoJSON Polygon | MultiPolygon — deck.gl reads it structurally.
  geometry: any;
  properties: RegionMetric;
}

export interface DeckLayerProps {
  rows: readonly i.IntelSignal[];
  viewport: Viewport;
  /** Geocoded contract aggregates — only consumed by the deprecated
   *  `contracts-hex` layer. */
  contracts?: readonly ContractMapPoint[];
  /** Region polygons + metrics from `/v1/map/regions` (geometry + values in
   *  one payload) — consumed by `contracts-region` via GeoJsonLayer. */
  regionFeatures?: readonly RegionFeature[];
  /** Active basemap variant — drives the per-basemap stroke (D-AS2). */
  basemapVariant?: 'terrain' | 'sat' | 'dark';
  /** Optional click handler from the consumer. */
  onPick?: (signal: i.IntelSignal | null) => void;
  /** Optional region click handler — fires when a `contracts-region`
   *  choropleth polygon is clicked. Receives the GeoJSON feature properties
   *  (region_id, name, contract_count, …) so the page can filter to it. */
  onRegionClick?: (props: RegionMetric) => void;
  /** Optional war-zone click handler — fires when a `war-zones` polygon is
   *  clicked. War zones are not IntelSignal rows so they cannot go through
   *  `onPick`; this dedicated callback carries the curated metadata instead. */
  onWarZoneClick?: (zone: WarZoneInfo) => void;
}

/** Minimal shape surfaced by the war-zones layer onClick. Carries only what
 *  the UI needs to render a popup — no raw polygon coordinates. */
export interface WarZoneInfo {
  id: string;
  name: string;
  severity: number;
  tag: string;
  asOf: string;
}

export interface LegendEntry {
  swatch: string; // CSS color token, e.g. "var(--red)"
  label: string;
  hint?: string;
}

/**
 * Why does this layer's color exist? Drives how the legend groups it.
 *
 *   - 'affiliation' → MIL-STD-2525 friendly/hostile/neutral/unknown
 *   - 'severity'    → sequential ramp (low→hot)
 *   - 'category'    → categorical (sanctions=red, supply=magenta, …)
 *   - 'recency'     → time-since-observed (rare; overlays only)
 */
export type ColorAxis = 'affiliation' | 'severity' | 'category' | 'recency';

export interface LayerSpec {
  id: string;
  label: string;
  kind: LayerKind;
  signalTypes: readonly i.SignalType[];
  /** Primary color token (CSS var). Drives the legend swatch. */
  color: string;
  /** Why this layer is colored the way it is. Drives legend grouping. */
  colorAxis: ColorAxis;
  /** Short hint shown in the legend tooltip (≤80 chars). */
  description: string;
  classification: Classification;
  legend: LegendEntry;
  /** Pure filter: returns just the rows this layer cares about. */
  select: (rows: readonly i.IntelSignal[]) => i.IntelSignal[];
  /** Build a deck.gl Layer instance from selected rows + viewport. */
  deckLayer: (props: DeckLayerProps) => Layer | null;
  /** Render gate by zoom level. Caller respects this to avoid overdraw. */
  visibleAtZoom: (zoom: number) => boolean;
  /** Default visibility for type-less layers (signalTypes.length === 0). For
   *  type-based layers, the `signal_type` filter still owns visibility. */
  defaultEnabled?: boolean;
}

import { conflictsLayer } from './conflicts.js';
import { contractsRegionLayer } from './contracts-region.js';
import { densityLayer } from './density.js';
import { heatmapLayer } from './heatmap.js';
import { newsLayer } from './news.js';
import { sanctionsLayer } from './sanctions.js';
import { scatterLayer } from './scatter.js';
import { seismicLayer } from './seismic.js';
import { supplyLayer } from './supply.js';
import { warZonesLayer } from './war-zones.js';
import { wildfireLayer } from './wildfire.js';

// Order matters — earlier entries render BELOW later entries. War zones
// are persistent context polygons that sit beneath the live signal
// markers so individual conflict events still pop on top. The contracts
// region choropleth sits LOW in the stack (just above war zones) — it's a
// large-area fill, so it must render beneath the intel signal markers and
// density hexes, otherwise its translucent fill would mute the foreground
// markers. (The old contracts-hex KDE sat higher because it was a sparse
// glow; a full-coverage polygon fill needs to go to the back.)
export const INTEL_LAYERS: readonly LayerSpec[] = [
  warZonesLayer,
  contractsRegionLayer,
  // Low-zoom default point layer — renders above the region choropleth so
  // dots pop over the fill (registry order = draw order). Replaces the
  // broken-shader heatmap as the z<5 default; the heatmap stays registered
  // (re-enableable from the legend) but off the default render path.
  scatterLayer,
  heatmapLayer,
  densityLayer,
  sanctionsLayer,
  conflictsLayer,
  supplyLayer,
  seismicLayer,
  wildfireLayer,
  // News sits last (renders on top) but defaults to a LOW-severity tint so it
  // reads as background context rather than competing with event markers.
  newsLayer,
];

export function findLayer(id: string): LayerSpec | undefined {
  return INTEL_LAYERS.find((l) => l.id === id);
}

/**
 * The set of `signal_type`s that already have a dedicated icon layer (SEI,
 * SAN, CFL, SUP, FIR, NEW, …). Derived from the registry so it stays correct
 * as icon layers are added/removed — no hand-maintained list to drift.
 *
 * The type-less scatter overlay (`scatter.ts`) excludes these so each signal
 * renders exactly once: as its dedicated toggleable icon, never also as an
 * always-on scatter dot. Lazily computed on first call (the registry array is
 * fully initialized by then) so importing this from a layer module that the
 * registry itself imports doesn't hit a module-init cycle.
 */
let dedicatedTypeSet: Set<i.SignalType> | null = null;
export function typesWithDedicatedLayer(): ReadonlySet<i.SignalType> {
  if (dedicatedTypeSet) return dedicatedTypeSet;
  const s = new Set<i.SignalType>();
  for (const layer of INTEL_LAYERS) {
    // Only per-type marker layers own a signal type. Aggregate / type-less
    // layers (scatter, heatmap, density, contracts-region) declare an empty
    // `signalTypes`, so they contribute nothing here.
    if (layer.kind === 'icon') {
      for (const t of layer.signalTypes) s.add(t);
    }
  }
  dedicatedTypeSet = s;
  return dedicatedTypeSet;
}
